import { useMediaQuery } from '@material-ui/core';
import React, { useState } from 'react';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { observer } from 'mobx-react-lite';
import { bopisState, setStoreSelection } from '../Bopis/bopisState';
import { IStoreAvailabilityData, StoreLocatorType } from './storeLocatorTypes';
import './style.scss';
import StockIndicator from '../StockIndicator/StockIndicator';
import AloRadioButton from 'src/scripts/templates/_shared/ui-components/YogaUI/AloRadioButton';
import {
  extractStoresDataFromBopisProductAvailabilityData,
  getInStoreStoreFromAvailabilityDetailsByLocationId,
  getPickupAvailabilityStatusText,
  checkInStoreDataAvailableInBopis,
} from '../Bopis/bopisUtils';
import { StoreLocatorTileTitle } from './StoreLocatorTileTitle';

let StoreLocatorTile = ({
  page,
  store,
  inputName,
  isAvailableOnline,
  showFullTile = false,
  storeSelectionEnabled = false,
  storeLocatorType,
  shouldShowOmniPdpExperience = false,
}: {
  page?: 'pdp' | 'cart';
  store: IStoreAvailabilityData;
  inputName: string;
  isAvailableOnline: boolean;
  showFullTile?: boolean;
  storeSelectionEnabled?: boolean;
  storeLocatorType: StoreLocatorType;
  shouldShowOmniPdpExperience?: boolean;
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const toggleDetails = () => {
    setShowDetails((prevShowDetails) => !prevShowDetails);
  };
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  function trimNumbersFromString(name: string) {
    const trimmedString = name.replace(/\d+/g, '');
    return trimmedString.trim();
  }

  return (
    <div
      className={`store-locator-tile ${storeSelectionEnabled ? 'pointer-events-enabled' : ''}`}
      {...(storeSelectionEnabled && !isMobile ? { onClick: () => setStoreSelection([store]) } : {})}
    >
      <div className="store-locator-tile__first">
        <div
          className="store-locator-tile__column"
          {...(storeSelectionEnabled && isMobile ? { onClick: () => setStoreSelection([store]) } : {})}
        >
          {shouldShowOmniPdpExperience ? (
            <StoreLocatorTileTitle store={store} storeLocatorType={storeLocatorType} />
          ) : (
            <div className="store-locator-tile__title-column">
              {storeSelectionEnabled ? (
                <AloRadioButton
                  label={trimNumbersFromString(store.name)}
                  groupName={`selectedStore-${inputName}`}
                  value={store.location_id}
                  checked={bopisState.storeSelection?.[0]?.location_id === store.location_id || false}
                  stopPropagation={true}
                />
              ) : store.name ? (
                <p className="black-text">{trimNumbersFromString(store.name)}</p>
              ) : null}
            </div>
          )}
          {page === 'pdp' ? (
            <PdpIndicators store={store} isAvailableOnline={isAvailableOnline} storeLocatorType={storeLocatorType} />
          ) : (
            <DefaultIndicator store={store} isAvailableOnline={isAvailableOnline} storeLocatorType={storeLocatorType} />
          )}
        </div>
        {showFullTile ? (
          isMobile ? (
            <button className="accordion-icon" type="button" onClick={toggleDetails}>
              {showDetails ? <span className="alo-subtract-icon"></span> : <span className="alo-add-icon"></span>}
            </button>
          ) : null
        ) : null}
      </div>
      {store.address && showFullTile && (
        <div className={`store-locator-tile__row ${isMobile && showDetails ? '' : 'hide'}`}>
          <span className="alo-location-pin-icon"></span>
          <div>
            <p>{store.address.address1 && store.address.address1}</p>
            <p>{store.address.address2 && store.address.address2}</p>
            <p>
              {store.address.city && <span>{store.address.city}</span>}
              {store.address.province_code && <span>, {store.address.province_code}</span>}
              {store.address.postal_code && <span> {store.address.postal_code}</span>}
            </p>
          </div>
        </div>
      )}
      {store.hours_of_operation.length > 0 && showFullTile ? (
        <div className={`store-locator-tile__row ${isMobile && showDetails ? '' : 'hide'}`}>
          <span className="alo-clock-icon"></span>
          <div>
            {store.hours_of_operation.map((row, index) => {
              return (
                <div className="store-locator-hours" key={index}>
                  <p className="store-locator-hours__row">{row.day_text}:</p>
                  <p className="store-locator-hours__row">{row.hour_text}</p>
                </div>
              );
            })}
          </div>
        </div>
      ) : showFullTile && !isMobile ? (
        <div className="store-locator-tile__row"> </div>
      ) : null}
    </div>
  );
};

const DefaultIndicator = ({
  store,
  isAvailableOnline,
  storeLocatorType,
}: {
  store: IStoreAvailabilityData;
  isAvailableOnline: boolean;
  storeLocatorType: StoreLocatorType;
}) => {
  const textWhenAvailable =
    storeLocatorType === 'bopis'
      ? isAvailableOnline
        ? getPickupAvailabilityStatusText(store.pickup_time_in_days ?? -1)
        : 'Available In Store'
      : 'In Stock';

  return (
    <StockIndicator
      isAvailable={store.available_after_safety_stock > 0}
      textWhenAvailable={textWhenAvailable}
      textWhenUnavailable="Out of Stock"
    />
  );
};

const PdpIndicators = ({
  store,
  isAvailableOnline,
  storeLocatorType,
}: {
  store: IStoreAvailabilityData;
  isAvailableOnline: boolean;
  storeLocatorType: StoreLocatorType;
}) => {
  const isInStoreDataAvailableInBopis = checkInStoreDataAvailableInBopis(bopisState.pdpStoresData);
  const inStoreDataAvailabilityList = extractStoresDataFromBopisProductAvailabilityData(
    bopisState.pdpStoresData,
    'instore'
  );
  const inStoreData =
    storeLocatorType === 'bopis'
      ? getInStoreStoreFromAvailabilityDetailsByLocationId(inStoreDataAvailabilityList, store.location_id)
      : store;

  const hideStoreIndicator = storeLocatorType === 'bopis' && !isInStoreDataAvailableInBopis;

  return (
    <>
      {storeLocatorType === 'bopis' && (
        <StockIndicator
          isAvailable={isAvailableOnline && store.available_after_safety_stock > 0}
          textWhenAvailable={getPickupAvailabilityStatusText(store.pickup_time_in_days ?? -1, true) + ' - Available'}
          textWhenUnavailable="Pickup - Not Available"
        />
      )}
      {hideStoreIndicator ? null : (
        <StockIndicator
          isAvailable={!!inStoreData && inStoreData.available_after_safety_stock > 0}
          textWhenAvailable="In Store - In Stock"
          textWhenUnavailable="In Store - Out of Stock"
        />
      )}
    </>
  );
};

StoreLocatorTile = observer(StoreLocatorTile);
export { StoreLocatorTile };
